import { useEffect, useMemo, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  batchZones, getWarehousePlan, isDerivedZone, listStorageLocations, listZones, ZoneBatchError, type ZoneRow,
} from "../../../api/rest";
import { useUnsavedGuard } from "../../../hooks/useUnsavedGuard";
import { useSiteId } from "../../../hooks/useSiteId";
import { clearFloorFrameCache } from "../../../hooks/useFloorFrame";
import { ROLE_RANK, useSiteStore } from "../../../stores/siteStore";
import { SplitView } from "../../../app/layout/SplitView";
import { ZoneCanvas } from "./zoneEditor/ZoneCanvas";
import { ZonePropertiesPanel } from "./zoneEditor/ZonePropertiesPanel";
import { initialState, isDirty, reduce, type Tool } from "./zoneEditor/zoneEditorState";
import { diffZones, toEditable } from "./zoneEditor/zoneGeometry";
import table from "../../WarehouseAdminPage.module.css";
import css from "./ZonesSection.module.css";

const EMPTY_ROWS: ZoneRow[] = [];

/**
 * ADR 403 — the zone editor. Hand-authored zones are drawn over the storage
 * footprints and saved as ONE transactional diff; derived aisle zones (`az:`,
 * ADR 213) are listed and drawn locked — the Grafo section owns them.
 */
export function ZonesSection() {
  const { warehouseId = "" } = useParams();
  // Key-remount (ADR 136): a warehouse switch must not carry A's dirty edits to B's Salvar.
  return <ZonesEditor key={warehouseId} warehouseId={warehouseId} />;
}

function ZonesEditor({ warehouseId }: { warehouseId: string }) {
  const { t, i18n } = useTranslation();
  const siteId = useSiteId();
  const qc = useQueryClient();
  const canEdit = useSiteStore((s) => s.isGlobalAdmin || s.roleRank >= ROLE_RANK["site-admin"]);

  const zonesQ = useQuery({ queryKey: ["zones", siteId, warehouseId], queryFn: () => listZones(siteId, warehouseId), retry: 0 });
  const footQ = useQuery({ queryKey: ["storage-locations-raw", siteId, warehouseId], queryFn: () => listStorageLocations(siteId, warehouseId, { raw: true }), retry: 0 });
  const planQ = useQuery({ queryKey: ["warehouse-plan", siteId, warehouseId], queryFn: () => getWarehousePlan(siteId, warehouseId), retry: 0 });

  const [state, dispatch] = useReducer(reduce, initialState);
  const dirty = isDirty(state);
  const rows = zonesQ.data ?? EMPTY_ROWS;
  // Everything the editor cannot edit stays visible: derived aisles and hand-authored locked rows.
  const locked = useMemo(() => rows.filter((r) => toEditable(r) == null), [rows]);
  const lockedDrawn = useMemo(() => locked.filter((r) => r.polygon != null), [locked]);

  // Guarded adjust-during-render (ADR 136): load fresh server data only when
  // nothing is dirty; while dirty the edits win and `stale` is derived below.
  const [loadedAt, setLoadedAt] = useState(0);
  if (zonesQ.dataUpdatedAt && zonesQ.dataUpdatedAt !== loadedAt && !dirty) {
    setLoadedAt(zonesQ.dataUpdatedAt);
    dispatch({ type: "load", zones: rows.flatMap((r) => { const e = toEditable(r); return e ? [e] : []; }) });
  }
  const stale = dirty && zonesQ.dataUpdatedAt !== 0 && zonesQ.dataUpdatedAt !== loadedAt;

  const { nudge } = useUnsavedGuard(dirty);
  // Guarded adjust-during-render: a refused tab switch bumps `nudge`; flash the bar briefly.
  const [seenNudge, setSeenNudge] = useState(nudge);
  const [flash, setFlash] = useState(false);
  if (nudge !== seenNudge) {
    setSeenNudge(nudge);
    if (dirty) setFlash(true);
  }
  useEffect(() => {
    if (!flash) return;
    const id = window.setTimeout(() => setFlash(false), 1200);
    return () => window.clearTimeout(id);
  }, [flash, nudge]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [cursor, setCursor] = useState<[number, number] | null>(null);
  const footprints = useMemo(() => (footQ.data ?? []).flatMap((l) => (l.polygon ? [l.polygon] : [])), [footQ.data]);

  const diff = useMemo(() => diffZones(state.original, state.working), [state.original, state.working]);
  const counts = useMemo(() => {
    const before = new Set(state.original.map((z) => z.id));
    const created = diff.upsert.filter((u) => !before.has(u.id)).length;
    return { created, updated: diff.upsert.length - created, deleted: diff.delete.length };
  }, [diff, state.original]);

  async function save() {
    setSaving(true); setError(null);
    try {
      await batchZones(siteId, warehouseId, diff);
      dispatch({ type: "saved" });
      clearFloorFrameCache();
      setSavedAt(Date.now());
      await qc.invalidateQueries({ queryKey: ["zones", siteId, warehouseId] });
    } catch (e) {
      // ZoneBatchError names the refused zone (ADR 403): say which, and select it when it is on the canvas.
      const zoneId = e instanceof ZoneBatchError ? e.zoneId : null;
      const message = e instanceof Error ? e.message : String(e);
      setError(zoneId ? `${message} (${zoneId})` : message);
      if (zoneId && state.working.some((z) => z.id === zoneId)) dispatch({ type: "select", id: zoneId });
    } finally { setSaving(false); }
  }

  useEffect(() => {
    if (savedAt == null) return;
    const id = window.setTimeout(() => setSavedAt(null), 4000);
    return () => window.clearTimeout(id);
  }, [savedAt]);

  const selected = state.working.find((z) => z.id === state.selectedId) ?? null;
  const toolBtn = (tool: Tool, label: string) => (
    <button type="button" disabled={saving} aria-pressed={state.tool === tool} onClick={() => dispatch({ type: "setTool", tool })}>{label}</button>
  );
  const fmt = (v: number) => v.toLocaleString(i18n.language, { maximumFractionDigits: 1 });

  const list = (
    <>
      <p className={css.note}>{t("structure.zones.note")}</p>
      {!canEdit && <p className={css.note}>{t("structure.zones.readOnly")}</p>}
      <table className={table.table} data-testid="zones-table">
        <thead><tr>
          <th>{t("structure.zones.col.id")}</th><th>{t("structure.zones.col.kind")}</th><th>{t("structure.zones.col.maxSpeed")}</th>
        </tr></thead>
        <tbody>
          {locked.map((z) => {
            const isDerived = isDerivedZone(z.id) || z.nodeId != null;
            return (
            <tr key={z.id} title={isDerived ? t("structure.zones.derived.tip") : undefined}>
              <td>{z.id}<span className={css.badge}>{t(isDerived ? "structure.zones.derived" : "structure.zones.locked")}</span></td>
              <td>{t(`zone.kind.${z.kind}`, { defaultValue: z.kind })}</td>
              <td>{z.maxSpeedMps != null ? fmt(z.maxSpeedMps) : "—"}</td>
            </tr>
            );
          })}
          {state.working.map((z) => (
            <tr key={z.id} className={`${css.clickRow} ${z.id === state.selectedId ? css.rowSelected : ""}`}
                onClick={() => dispatch({ type: "select", id: z.id })}>
              <td>{z.id}</td>
              <td>{t(`zone.kind.${z.kind}`)}</td>
              <td>{z.maxSpeedMps != null ? fmt(z.maxSpeedMps) : "—"}</td>
            </tr>
          ))}
          {rows.length === 0 && state.working.length === 0 && (
            <tr><td colSpan={3} className={css.empty}>
              {zonesQ.isError ? t("structure.zones.error") : t("structure.zones.empty")}
            </td></tr>
          )}
        </tbody>
      </table>
      <ZonePropertiesPanel
        zone={selected} disabled={!canEdit || saving}
        onKind={(kind) => dispatch({ type: "setKind", kind })}
        onMaxSpeed={(v) => dispatch({ type: "setMaxSpeed", v })}
        onDirection={(v) => dispatch({ type: "setDirection", v })}
        onDelete={() => dispatch({ type: "deleteSelected" })}
      />
    </>
  );

  const detail = (
    <div className={css.layout}>
      {canEdit ? (
        <div className={css.toolbar} role="toolbar" aria-label={t("structure.zones.canvas.aria")}>
          {toolBtn("select", t("structure.zones.tool.select"))}
          {toolBtn("rect", t("structure.zones.tool.rect"))}
          {toolBtn("polygon", t("structure.zones.tool.polygon"))}
          <button type="button" disabled={saving} aria-pressed={state.snapOn} onClick={() => dispatch({ type: "toggleSnap" })}>{t("structure.zones.tool.snap")}</button>
        </div>
      ) : <div />}
      <ZoneCanvas
        state={state} dispatch={dispatch} editable={canEdit && !saving} fitKey={warehouseId}
        footprints={footprints}
        derived={lockedDrawn} plan={planQ.data ?? null} onCursor={setCursor}
      />
      <div className={css.status}>
        {cursor ? t("structure.zones.cursor", { x: fmt(cursor[0]), y: fmt(cursor[1]) }) : ""}
      </div>
      {canEdit && stale && (
        <div className={css.saveBar}>
          <span>{t("structure.zones.stale")}</span><span className={css.spacer} />
          <button type="button" className={css.ghost} onClick={() => { dispatch({ type: "discard" }); setError(null); void zonesQ.refetch(); }}>
            {t("structure.zones.reload")}
          </button>
        </div>
      )}
      {canEdit && error && <div role="alert" className={css.alert}>{t("structure.zones.saveError", { message: error })}</div>}
      {canEdit && (dirty || savedAt != null) && (
        <div className={flash ? `${css.saveBar} ${css.flash}` : css.saveBar}>
          <span>{dirty ? t("structure.zones.dirty", counts) : t("structure.zones.saved")}</span>
          <span className={css.spacer} />
          {dirty && <button type="button" className={css.ghost} disabled={saving} onClick={() => { dispatch({ type: "discard" }); setError(null); }}>{t("structure.zones.discard")}</button>}
          {dirty && <button type="button" className={css.primary} disabled={saving} onClick={() => void save()}>{t("structure.zones.save")}</button>}
        </div>
      )}
    </div>
  );

  return (
    <div className={css.wideSplit}>
      <SplitView list={list} detail={detail} detailOpen detailLabel={t("structure.zones.canvas.aria")} />
    </div>
  );
}
